<script lang="ts">
    import { blur } from 'svelte/transition';
    import { onMount } from 'svelte';
    import { _ } from 'svelte-i18n';
    import { inview } from 'svelte-inview/dist/index';
    import { get } from 'svelte/store';
    import * as Sentry from '@sentry/browser';

    import { JobApi } from './JobApi';
    import { api, clientSettings } from '../../../stores/api';
    import { filter } from '../../../stores/filter';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import MessageDanger from '../../shared/Messages/MessageDanger.svelte';
    import type { JobWithRealisationAndRecentRejectionResponseType } from './types';
    import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
    import { onMapReady } from '../../utilities/onMapReady';
    import {
        JobsOnMap,
        getJobPin,
        getJobGroup,
        getJobLabel,
        getJobCluster,
    } from '../Jobs/JobsOnMap.js';
    import MessageFoundItems from '../../shared/MessageFoundItems.svelte';
    import SwitchView from '../../shared/SwitchView.svelte';
    import { debug, jobs } from '../../../stores/store';
    import ErrorInline from '../../shared/ErrorInline.svelte';
    import {
        getCountFilterQuery,
        getFilterQuery,
        resetFilterQuery,
    } from '../../utilities/getFilterQuery';
    import JobOneRow from './JobOneRow.svelte';
    import JobOneHeader from './JobOneHeader.svelte';
    import JobListFilter from './JobListFilter.svelte';
    import { captureMessage } from '../../utilities/Sentry';
    import { features } from '../../shared/Features/features';
    import { Navigation } from '../../utilities/Navigation';
    import { Job } from './Job';
    import { BalloonContentGenerator } from './BalloonContentGenerator';
    import { getJobColor, type JOB_COLOR_METHOD_TYPE, vehicleColors } from './getJobColor';
    import { VehiclesApi } from '../Vehicles/VehiclesApi';
    import type { QuickFilterType } from '../../shared/QuickFilter.svelte';

    export let showMonit: boolean = false;

    let loading: boolean = false;
    let loadingAfterScroll: boolean = false;
    let error: string = '';
    let errorSave: string = '';

    const initialLimit = 20;
    let jobFilter: ApiFilterBuilder = new ApiFilterBuilder(initialLimit);
    let limit: number = initialLimit;
    let offset: number = 0;
    let hasMore = true;
    let reloadMap: boolean = true;

    let expandedIndex: number = -1;
    let lastExpandedJobId: number | null = null;
    let letEditAllFiltered: boolean = false;
    let onlyLowQualityAddresses: boolean = false;
    let selectedTargetDate: string = new Date().toISOString().substring(0, 10);
    let zoomBestFitTimeout: any = null;

    let componentMounted: boolean = false;

    let updateRealisationSince: number = 0;
    let updateRealisationTimeout: any = null;
    let selected_job_color_method_loaded: JOB_COLOR_METHOD_TYPE | null = null;

    let quickFilters: QuickFilterType[] = [
        {text: $_('unassigned'), filter: { process_stage: { $lt: 20 }, completed_as_planned: null }, showCount: true, updateFilter: true, active: true, class: ''},
        {text: $_('not_geocoded'), filter: { coords_accepted: false, completed_as_planned: null }, showCount: true, updateFilter: true, active: false, class: (count) => count > 0 ? 'error' : ''}
    ];

    function updateRealisation() {
        let filter: ApiFilterBuilder = new ApiFilterBuilder(-1);
        let queries: any = { ...getFilterQuery() };
        queries['$and'] = [
            { ...getFilterQuery() },
            {
                $or: [
                    {'job.completed_as_planned': null},
                    {'job.completed_as_planned': {
                            $gte: {
                                $expression:
                                    "now() - interval '5 days'",
                            },
                        }
                    }
                ]
            },
            {
                $or: [
                    {
                        'assignment_job.updated': {
                            $gte: {
                                $expression:
                                    "now() - interval '" +
                                    Math.ceil(
                                        (1.2 * (Date.now() - updateRealisationSince)) / 1000
                                    ) +
                                    " seconds'",
                            },
                        },
                    },
                    {
                        'assignment_job_rejection.updated': {
                            $gte: {
                                $expression:
                                    "now() - interval '" +
                                    Math.ceil(
                                        (1.2 * (Date.now() - updateRealisationSince)) / 1000
                                    ) +
                                    " seconds'",
                            },
                        },
                    },
                ],
            },
        ];
        updateRealisationSince = Date.now();
        JobApi.getWithRealisationAndRecentRejectionByQueries($api, queries, filter).then(jobs => {
            let job: JobWithRealisationAndRecentRejectionResponseType | undefined;
            for (let i = 0, ii = $jobs.filteredJobs.length; i < ii; i++) {
                if ((job = jobs.find(job => job.id === $jobs.filteredJobs[i].id)) !== undefined) {
                    if (!jobMapObjects[job.id]) {
                        jobMapObjects[job.id] = {};
                    }
                    jobMapObjects[job.id].detailsLoaded = false;
                    $jobs.filteredJobs[i] = job;
                }
            }
            clearTimeout(updateRealisationTimeout);
            updateRealisationTimeout = setTimeout(updateRealisation, 60000);
        });
    }

    function reloadElements() {
        $jobs.selectedOrUnselected = [];
        $jobs.selectMode = 'selected';
        if (componentMounted && jobsOnMap) {
            jobMapObjects = {};
            try {
                jobsOnMap.setFilter(getFilterQuery());
                if (zoomBestFitTimeout) {
                    clearTimeout(zoomBestFitTimeout);
                }
                if ($clientSettings.display.zoom_map_on_every_change) {
                    zoomBestFitTimeout = setTimeout(() => {
                        jobsOnMap.zoomBestFit(true);
                    }, 250);
                }
            } catch (err) {
                console.error('reloadElements', err);
                resetFilterQuery();
            }
        }
        updateRealisationSince = Date.now();
        Promise.all([
            JobApi.getWithRealisationAndRecentRejectionByQueries($api, getFilterQuery(), jobFilter),
            JobApi.getCountWithQueries($api, getFilterQuery(), jobFilter),
            JobApi.getCountWithQueries($api, getCountFilterQuery(), jobFilter),
        ])
            .then(response => {
                $jobs.filteredJobs = response[0];
                hasMore = response[0].length !== 0;
                offset = 0;
                $jobs.countFiltered = response[1];
                $jobs.countTotal = response[2];
                expandedIndex = -1;
                letEditAllFiltered =
                    Object.keys(getFilterQuery()).length > 0 &&
                    $jobs.filteredJobs.length > 0 &&
                    !hasMore;
                if (updateRealisationTimeout) {
                    clearTimeout(updateRealisationTimeout);
                }
                updateRealisationTimeout = setTimeout(updateRealisation, 60000);
                if ($features.feature_ux_improvements) {
                    getQuickFilterCounts();
                }
            })
            .catch(err => {
                error = err;
                resetFilterQuery();
            });
    }

    function onFilterChange() {
        if (offset + limit > $jobs.filteredJobs.length) {
            offset = 0;
        }
        $jobs.selectedOrUnselected = [];
        $jobs.selectMode = 'selected';
        expandedIndex = -1;
        reloadElements();
    }

    // Will be called when we've scrolled to the pont where more data needs to be fetched
    const handleChange = (e: any) => {
        if (e.detail.inView && hasMore) {
            loadingAfterScroll = true;
            offset = offset + limit;
            jobFilter.paginate(limit, 0);
            let tempJobFilter = Object.assign(
                Object.create(Object.getPrototypeOf(jobFilter)),
                jobFilter
            );
            JobApi.getWithRealisationAndRecentRejectionByQueries(
                $api,
                getFilterQuery(),
                tempJobFilter.paginate(limit, offset)
            )
                .then(response => {
                    $jobs.filteredJobs = [...$jobs.filteredJobs];
                    // $jobs.selectAllChecked = false;
                    response.forEach(job => {
                        if (!$jobs.filteredJobs.find(filteredJob => filteredJob.id === job.id)) {
                            $jobs.filteredJobs.push(job);
                        }
                    });
                    hasMore = response.length !== 0;
                    letEditAllFiltered =
                        Object.keys(getFilterQuery()).length > 0 &&
                        $jobs.filteredJobs.length > 0 &&
                        !hasMore;
                    // $jobs.visibleJobs = [...filteredJobs.map( (job) => job.id)];
                })
                .finally(() => {
                    loadingAfterScroll = false;
                });
        }
    };

    let onClickedJobPoint = function (start: boolean) {
        // This is not needed - in svelte components we don`t need to use document.getElementById or document.querySelector
        // if (clickedJob) {
        // let overlay = document.getElementById('job-list-overlay');
        // console.log('!!! onClickedJobPoint', start, clickedJob, overlay, overlay?.style.display);
        // if (start) {
        // if (overlay && overlay.style.display != 'none') {
        //     overlay.style.display = 'none';
        // }
        // } else {
        // if (overlay && overlay.style.display == 'none') {
        //     overlay.style.display = 'flex';
        // }
        // }
        // }
    };

    let onJobCoordsSet = function (id: number | null | undefined) {
        if (typeof id == 'number') {
            delete jobMapObjects[id];
            jobsOnMap.jobReloadSingle(id);
        } else {
            jobMapObjects = {};
            jobsOnMap.jobsReload();
        }
    };

    // let clickedJob: any;
    let jobsOnMap: JobsOnMap;
    let jobMapObjects: any = {};
    let today = (function () {
        let d = new Date();
        return (
            d.getFullYear() +
            '-' +
            ('0' + (d.getMonth() + 1)).slice(-2) +
            '-' +
            ('0' + d.getDate()).slice(-2)
        );
    })();

    const getJobMapObject = (job: any) => {
        let mapObj;
        let zIndex = 502 + 90 - job.geom.coordinates[1]; // Make icons place like for real
        if (job.count && job.count > 1) {
            mapObj = new Targeo.MapObject.Point(
                { x: job.geom.coordinates[0], y: job.geom.coordinates[1] },
                getJobGroup('#ffffff', 30 + (job.count.toString().length - 1) * 10, {
                    total: job.count,
                    filtered: job.filter,
                })
            );
        } else {
            let coordkey = JSON.stringify(job.geom.coordinates);
            if (!(job.id in jobMapObjects) || jobMapObjects[job.id].coords != coordkey) {
                let color = getJobColor(
                    job,
                    today,
                    $clientSettings.display.selected_job_color_method
                );
                mapObj = new Targeo.MapObject.Point(
                    { x: job.geom.coordinates[0], y: job.geom.coordinates[1] },
                    getJobPin(
                        color,
                        job.filter === false
                            ? JobsOnMap.pinSizes.unmatching
                            : job.filter === true
                              ? JobsOnMap.pinSizes.matching
                              : JobsOnMap.pinSizes.normal,
                        { type: job.doc },
                        $clientSettings.display.icon_style
                    ),
                    'job_' + job.id,
                    {
                        name: getJobLabel(job),
                        cluster: getJobCluster(
                            color,
                            job.filter === false
                                ? JobsOnMap.pinSizes.unmatching
                                : job.filter === true
                                  ? JobsOnMap.pinSizes.matching
                                  : JobsOnMap.pinSizes.normal,
                            { type: job.doc },
                            $clientSettings.display.icon_style
                        ),
                        zIndex: zIndex,
                        balloon: {
                            width: 410,
                            body: '<div class="lds-dual-ring-improved" style="width: 60px; height: 60px;"></div>',
                        },
                        mouseButtonActions: {
                            left: function (mapObj: any) {
                                let jobId = parseInt(mapObj.id.slice(4));
                                console.log('JobList, point on map clicked', mapObj.id, jobId);
                                if (expandedIndex === jobId) {
                                    closeJob(jobId);
                                } else {
                                    openJob(jobId);
                                }
                                // $jobs = $jobs;
                                // JobApi.getOne($api, jobId)
                                //     .then(job => {
                                //         clickedJob = { job: job };
                                //     })
                                //     .catch(err => {
                                //         error = err;
                                //         resetFilterQuery();
                                //     });
                            },
                        },
                    }
                );
                jobMapObjects[job.id] = {
                    coords: coordkey,
                    mapObject: mapObj,
                    detailsLoaded: false,
                    balloonListener: null,
                    color: color,
                    pinOpts: { type: job.doc },
                };
            } else {
                mapObj = jobMapObjects[job.id].mapObject;
            }
            if (!jobMapObjects[job.id].detailsLoaded) {
                if (jobMapObjects[job.id].balloonListener) {
                    jobMapObjects[job.id].mapObject.unlistenByKey(
                        jobMapObjects[job.id].balloonListener
                    );
                }
                jobMapObjects[job.id].balloonListener = mapObj.listen(
                    'onBalloonShow',
                    function (ev: any) {
                        if (!jobMapObjects[job.id].detailsLoaded) {
                            var jobId = parseInt(ev.target.id.slice(4));
                            JobApi.getOneWithRealisationAndRecentRejection($api, jobId)
                                .then(job => {
                                    let balloonContentGenerator = new BalloonContentGenerator(
                                        new Job().set(job)
                                    );
                                    let html = balloonContentGenerator.getBalloonHtmlArray();
                                    jobMapObjects[job.id].mapObject.setBalloonOptions({
                                        body: html.join(''),
                                    });
                                    Targeo.Base.Map.getObjectManager().refreshBalloon(
                                        'job_' + job.id
                                    );
                                    balloonContentGenerator.connectBalloonThumbnails();
                                    jobMapObjects[job.id].detailsLoaded = job;
                                })
                                .catch(err => {
                                    error = err;
                                    resetFilterQuery();
                                });
                        } else {
                            let balloonContentGenerator = new BalloonContentGenerator(
                                new Job().set(jobMapObjects[job.id].detailsLoaded)
                            );
                            balloonContentGenerator.connectBalloonThumbnails();
                        }
                    }
                );
            }
        }
        return mapObj;
    };

    const emphasiseJobOnMap = (jobId: number, emphasise: boolean) => {
        if (jobMapObjects[jobId] && jobMapObjects[jobId].mapObject) {
            jobMapObjects[jobId].mapObject.setIcon(
                getJobPin(
                    jobMapObjects[jobId].color,
                    emphasise ? JobsOnMap.pinSizes.matching : JobsOnMap.pinSizes.normal,
                    jobMapObjects[jobId].pinOpts,
                    $clientSettings.display.icon_style
                )
            );
        }
    };

    function openJob(jobId: number) {
        if (lastExpandedJobId !== null) {
            emphasiseJobOnMap(lastExpandedJobId, false);
        }
        lastExpandedJobId = jobId;
        emphasiseJobOnMap(jobId, true);
        if (!$jobs.filteredJobs.find(job => job.id === jobId)) {
            //. load job in not loaded in the list (dynamically)
            JobApi.getOne($api, jobId)
                .then(job => {
                    $jobs.filteredJobs = [...$jobs.filteredJobs, job];
                    expandedIndex = jobId; // toggle job to open after loaded
                    // scrollToJob(jobId);
                    setTimeout(() => {
                        scrollToJob(jobId);
                    }, 50);
                })
                .catch(err => {
                    error = err;
                    console.error('JobList.openJob', err);
                });
        } else {
            expandedIndex = jobId; // toggle job to open if already loaded
            setTimeout(() => {
                scrollToJob(jobId);
            }, 50);
        }
    }

    function closeJob(jobId: number) {
        expandedIndex = -1;
        emphasiseJobOnMap(jobId, false);
    }

    function scrollToJob(jobId: number) {
        const jobElement = document.getElementById(`job-${jobId}`);
        if (jobElement) {
            jobElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    function updateTargetDate() {
        JobApi.modify(get(api), getFilterQuery(), { target_date: selectedTargetDate })
            .then(response => {
                letEditAllFiltered = false;
                onFilterChange();
            })
            .catch(err => {
                error = err?.translation_id || err;
            });
    }

    function getQuickFilterCounts() {
        let requests = [];
        for (let i = 0; i < quickFilters.length; i++) {
            if (quickFilters[i].showCount) {
                requests.push(JobApi.getCountWithQueries($api, quickFilters[i].filter, jobFilter));
            } else {
                requests.push(null);
            }
        }
        if (requests.filter((r) => r !== null).length) {
            Promise.all(requests).then(response => {
                $jobs.quickFilters.counts = response;
            });
        }
    }

    onMount(() => {
        $jobs.quickFilters.filters = quickFilters;
        componentMounted = true;
        Promise.all([onMapReady(), VehiclesApi.getColors($api)])
            .then(_ => {
                $vehicleColors = _[1];
                if (componentMounted) {
                    jobsOnMap = new JobsOnMap({
                        type: 'jobs',
                        getMapObject: getJobMapObject,
                        projection: [
                            'id',
                            'geom',
                            'process_status',
                            'target_date',
                            'start_date',
                            'end_date',
                            'special_attributes>delivery_or_collection@doc',
                            'special_attributes>parcels@parcels',
                            'assigned_vehicle_id',
                            'vehicle_id',
                            'address_attributes'
                        ],
                        filter: getFilterQuery(),
                        maxPointZoom: 32,
                        addJob: true,
                    });
                    jobsOnMap.addEventListener('jobadd', (evt: any) => {
                        console.log('!!! addJob event', evt.detail);
                        $jobs.revGeoDetails = evt.detail;
                        Navigation.navigate('/manual-add-job/');
                    });
                    if ($clientSettings.display.zoom_map_on_every_change) {
                        jobsOnMap.zoomBestFit(true);
                    }
                    selected_job_color_method_loaded =
                        $clientSettings.display.selected_job_color_method;
                }
            })
            .catch(err => {
                error = err;
                resetFilterQuery();
            });

        return () => {
            componentMounted = false;
            if (jobsOnMap) {
                jobMapObjects = {};
                jobsOnMap.destroy();
            } else {
                captureMessage('captureMessage: JobsList.onMount: jobsOnMap not ready');
            }
        };
    });

    $: {
        if (!error) {
            $filter.jobsToBeCompleted.queries = $filter.jobsToBeCompleted.queries;
            reloadElements();
        }
    }

    $: {
        if ($jobs.forceRecount) {
            $jobs.forceRecount = false;
            $jobs.selectedOrUnselected = [];
            $jobs.selectMode = 'selected';
            Promise.all([
                JobApi.getCountWithQueries($api, getFilterQuery(), jobFilter),
                JobApi.getCountWithQueries($api, {}, jobFilter),
            ])
                .then(response => {
                    $jobs.countFiltered = response[0];
                    $jobs.countTotal = response[1];
                })
                .catch(err => {
                    error = err;
                    resetFilterQuery();
                });
        }
    }

    $: {
        if ($jobs.forceReload) {
            $jobs.forceReload = false;
            $jobs.selectedOrUnselected = [];
            $jobs.selectMode = 'selected';
            reloadElements();
            if (error) {
                resetFilterQuery();
            }
        }
    }

    $: {
        if (
            jobsOnMap &&
            selected_job_color_method_loaded !== $clientSettings.display.selected_job_color_method
        ) {
            console.log(
                'Recreating jobsOnMap',
                $clientSettings.display.selected_job_color_method,
                selected_job_color_method_loaded
            );
            selected_job_color_method_loaded = $clientSettings.display.selected_job_color_method;
            jobMapObjects = {};
            try {
                jobsOnMap.setFilter(getFilterQuery());
            } catch (err) {
                console.error('reloadElements', err);
                resetFilterQuery();
            }
        }
    }
</script>

{#if error}
    <MessageDanger message={$_('fetch_jobs_failed')} bind:error />
{:else}
    {#if errorSave}
        <ErrorInline text={errorSave} />
    {/if}

    {#if loading}
        <div in:blur={{ delay: 500, duration: 1000 }}>
            <Spinner />
        </div>
    {:else}
        <!--{#if $jobs.countTotal.count>0}-->
        <div style="margin-top: 10px;" class:blur={showMonit}>
            <JobListFilter
                makeBlured={false}
                filteredCount={$jobs.countFiltered.count}
                totalCount={$jobs.countTotal.count}
                {quickFilters}
                onQuickFilterSwitch={(index, active) => {
                    $jobs.forceReload = true;
                }}
            />
        </div>
        <!--{/if}-->

        {#if $jobs.countTotal.count > 0}
            <div style="flex:0; justify-content: flex-end; display: flex;">
                <span style="margin-left: auto;">
                    <SwitchView bind:checked={$clientSettings.display.simple_view} />
                </span>
            </div>
        {/if}

        {#if $debug}
            {JSON.stringify(jobFilter.getSortBy())}
            expandedIndex={expandedIndex}
        {/if}

        {#if $jobs.countTotal.count > 0}
            <JobOneHeader
                bind:jobFilter
                {onFilterChange}
                bind:simpleView={$clientSettings.display.simple_view}
            />
        {/if}

        <div class="job-list">
            {#each $jobs.filteredJobs as job, index (job.id)}
                <div in:blur|global id={`job-${job.id}`}>
                    <JobOneRow
                        enableCheckbox={$features.feature_allow_modify_even_job_is_assigned ||
                            new Job().set(job).editable()}
                        bind:job
                        index={job.id}
                        bind:expandedIndex
                        showHeader={false}
                        {jobsOnMap}
                        bind:simpleView={$clientSettings.display.simple_view}
                        on:setcoordinates={() => onJobCoordsSet(job.id)}
                        on:open={() => {
                            console.log('JobOneRow on:open');
                            openJob(job.id);
                        }}
                        on:close={() => {
                            console.log('JobOneRow on:close');
                            closeJob(job.id);
                            //clickedJob = null;
                        }}
                        on:change={() => {
                            try {
                                console.log('job.vehicle_id', job.vehicle_id);
                                jobMapObjects[job.id].color = getJobColor(
                                    job,
                                    today,
                                    $clientSettings.display.selected_job_color_method
                                );
                                emphasiseJobOnMap(job.id, true);
                            } catch (e) {
                                console.error('on:change: ', e);
                            }
                        }}
                    />
                </div>
            {/each}

            {#if loadingAfterScroll}
                <Spinner />
            {:else if hasMore}
                <div
                    class="inview-element"
                    use:inview={{}}
                    on:change={e => {
                        handleChange(e);
                    }}
                />
            {:else}
                <MessageFoundItems count={$jobs.filteredJobs.length} />
            {/if}
        </div>
    {/if}
{/if}

<style>
    .inview-element {
        height: 100px;
    }
    .job-list {
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        overflow-x: hidden;
        flex: 1;
        padding-bottom: 40px;
        margin-bottom: 20px;
    }
</style>
